import { User, Mail, Building, Shield, Calendar, Bell, Moon, Sun, LogOut, Info } from 'lucide-react';
import { format } from 'date-fns';
import { usePushNotifications } from '@/app/hooks/usePushNotifications';
import { useAuthStore } from '@/app/store/authStore';
import { useTheme } from '@/components/theme-provider';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileSwitch } from '@/components/ui/MobileSwitch';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileBadge } from '@/components/ui/MobileBadge';

export default function ProfilePage() {
    const user = useAuthStore((state) => state.user);
    const logoutAction = useAuthStore((state) => state.logoutAction);
    const { theme, setTheme } = useTheme();
    const {
        isSupported,
        isIOSBrowser,
        permission,
        isSubscribed,
        isLoading: pushLoading,
        subscribe,
        unsubscribe
    } = usePushNotifications();

    const getInitials = () => {
        const name = user?.full_name || user?.username || 'U';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handlePushToggle = async (enabled: boolean) => {
        if (enabled) {
            await subscribe();
        } else {
            await unsubscribe();
        }
    };

    const getPushStatusText = () => {
        if (isIOSBrowser) return 'Add to Home Screen first';
        if (!isSupported) return 'Not supported on this device';
        if (permission === 'denied') return 'Blocked in browser settings';
        if (isSubscribed) return 'Enabled';
        return 'Disabled';
    };

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader title="Profile" />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-6">
                    {/* Profile Header */}
                    <div className="mobile-card flex flex-col items-center py-8">
                        <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center mb-4">
                            <span className="text-3xl font-bold text-primary-foreground">
                                {getInitials()}
                            </span>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-1">
                            {user?.full_name || user?.username || 'User'}
                        </h2>
                        {user?.role?.name && (
                            <MobileBadge variant="secondary">
                                {user.role.name}
                            </MobileBadge>
                        )}
                    </div>

                    {/* Account Info */}
                    <div>
                        <h2 className="mobile-section-header">Account</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                        <User className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Username</p>
                                        <p className="font-medium">{user?.username || '-'}</p>
                                    </div>
                                </div>
                            </MobileCardRow>

                            {user?.email && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <Mail className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Email</p>
                                            <p className="font-medium">{user.email}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}

                            {user?.role?.name && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <Shield className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Role</p>
                                            <p className="font-medium">{user.role.name}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>
                    </div>

                    {/* Organization Info */}
                    {user?.organization && (
                        <div>
                            <h2 className="mobile-section-header">Organization</h2>
                            <MobileCardGroup>
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <Building className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Organization</p>
                                            <p className="font-medium">{user.organization.name}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Sites */}
                    {user?.sites && user.sites.length > 0 && (
                        <div>
                            <h2 className="mobile-section-header">Assigned Sites</h2>
                            <MobileCardGroup>
                                {user.sites.map((site) => (
                                    <MobileCardRow key={site.id}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <span className="text-sm font-medium text-primary">
                                                    {site.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="font-medium">{site.name}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                ))}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Settings Section */}
                    <div>
                        <h2 className="mobile-section-header">Settings</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Bell className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Push Notifications</p>
                                            <p className="text-xs text-muted-foreground">
                                                {getPushStatusText()}
                                            </p>
                                        </div>
                                    </div>
                                    {isSupported && permission !== 'denied' && (
                                        <MobileSwitch
                                            checked={isSubscribed}
                                            onCheckedChange={handlePushToggle}
                                            disabled={pushLoading}
                                        />
                                    )}
                                </div>
                            </MobileCardRow>

                            <MobileCardRow>
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            {theme === 'dark' ? (
                                                <Moon className="h-5 w-5 text-muted-foreground" />
                                            ) : (
                                                <Sun className="h-5 w-5 text-muted-foreground" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium">Dark Mode</p>
                                            <p className="text-xs text-muted-foreground">
                                                {theme === 'dark' ? 'Enabled' : 'Disabled'}
                                            </p>
                                        </div>
                                    </div>
                                    <MobileSwitch
                                        checked={theme === 'dark'}
                                        onCheckedChange={(isDark) => setTheme(isDark ? 'dark' : 'light')}
                                    />
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>

                    {/* About */}
                    <div>
                        <h2 className="mobile-section-header">About</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                        <Info className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium">Version</p>
                                        <p className="text-xs text-muted-foreground">1.0.0</p>
                                    </div>
                                </div>
                            </MobileCardRow>

                            {user?.created_at && (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <Calendar className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Member Since</p>
                                            <p className="font-medium">
                                                {format(new Date(user.created_at), 'MMMM d, yyyy')}
                                            </p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>
                    </div>

                    {/* Sign Out */}
                    <div className="pt-2 pb-4">
                        <MobileButton
                            variant="destructive"
                            size="lg"
                            className="w-full"
                            icon={<LogOut className="h-5 w-5" />}
                            onClick={logoutAction}
                        >
                            Sign Out
                        </MobileButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
